import moment from 'moment';

/**
 * Check whether the argument is a Promise or Function
 * indicating an API call is in progress.
 * @param data
 * @returns {boolean}
 */
export function isFetching(data) {
    if (data === null || data === false || data === undefined) {
        return false;
    }
    return Reflect.has(data, 'then') || typeof data === typeof Function;
}

/**
 * Check whether the data is expired. If the `time`
 * property is missing or past due in the target data
 * then it will be considered expired.
 * @param data
 * @returns {boolean}
 */
export function isExpired(data) {
    if (data === null || data === false || data === undefined) {
        return true;
    }
    const {time = 0} = data;
    return moment.utc().valueOf() > time;
}

/**
 * Keep request results in memory for 1 minute.
 * @type {number}
 */
const cacheTimeLimitMs = 60 * 1000;

/**
 * Wrap the data in an object with an added `time`
 * property used to track when the value was saved.
 * Works in conjunction with the asyncAction method.
 * @param data
 * @param limit? {number} Define in milliseconds the amount of time to keep
 * data in memory. By default the limit is one (1) minute. In some cases
 * a longer time to live is recommended.
 * @returns {{time: number}}
 */
export function timedSave(data, limit = cacheTimeLimitMs) {
    return {
        time: moment.utc().valueOf() + limit,
        ...data,
    };
}

/**
 * Generate a shared asynchronous action that can be called simultaneously from multiple
 * components and resolve only once for all requests.
 * @param bus
 * @param eventName
 * @param commitName
 * @param predicate
 * @param delegate
 * @param commit
 * @returns {*}
 */
export default function asyncAction({
    bus, eventName, commitName, predicate, delegate, commit,
}) {
    // if it's already fetching data return
    // a promise that will resolve once the
    // first fetch completes
    if (isFetching(predicate)) {
        return new Promise((resolve) => {
            bus.$once(eventName, () => {
                resolve();
            });
        });
        // otherwise check if expired, and start
        // an API fetch
    } if (isExpired(predicate)) {
        const storedFetch = async () => {
            await delegate();
            bus.$emit(eventName);
        };
        // store current unresolved request
        commit(commitName, storedFetch);
        return storedFetch();
    }
    // if neither expired or fetching
    // resolve immediately
    return true;
}
